/**
 * Center Service
 * Handles CRUD operations for centers/teams using Supabase backend
 */
import supabaseService from './supabaseService';
import settingsService from './settingsService';
import logger from '../utils/logger';
import { v4 as uuidv4 } from 'uuid';

const CENTERS_TABLE = 'centers';

class CenterService {
  async getAllCenters() {
    try {
      logger.info('Fetching all centers', { operation: 'getAllCenters' });

      const centers = await supabaseService.select(CENTERS_TABLE, {
        order: { column: 'name', ascending: true },
      });

      logger.info('Centers fetched successfully', {
        operation: 'getAllCenters',
        count: centers?.length || 0,
      });

      return centers || [];
    } catch (error) {
      logger.error('Error fetching centers', error, { operation: 'getAllCenters' });
      throw error;
    }
  }

  async getCenterById(centerId) {
    try {
      logger.info('Fetching center by ID', { centerId, operation: 'getCenterById' });

      const center = await supabaseService.select(CENTERS_TABLE, {
        filters: [{ column: 'id', value: centerId }],
        single: true,
      });

      if (!center) {
        logger.warning('Center not found', { centerId, operation: 'getCenterById' });
        return null;
      }

      return center;
    } catch (error) {
      logger.error('Error fetching center', error, { centerId, operation: 'getCenterById' });
      throw error;
    }
  }

  async createCenter(centerData) {
    try {
      logger.info('Creating new center', { operation: 'createCenter', data: centerData });

      if (!centerData.name) {
        throw new Error('Center name is required');
      }

      const existing = await supabaseService.select(CENTERS_TABLE, {
        filters: [{ column: 'name', operator: 'ilike', value: centerData.name }],
        single: true,
      });

      if (existing) {
        throw new Error('Center with this name already exists');
      }

      // Get system settings to inherit the yearlyTokenLimit
      const systemSettings = await settingsService.getSettings();
      const systemYearlyTokenLimit = systemSettings.yearlyTokenLimit || 12;

      // If yearlyTokenLimit is provided, validate it doesn't exceed system settings
      let yearlyTokenLimit = systemYearlyTokenLimit;
      if (centerData.yearlyTokenLimit !== undefined && centerData.yearlyTokenLimit !== null) {
        if (centerData.yearlyTokenLimit > systemYearlyTokenLimit) {
          throw new Error(
            `Center yearly token limit (${centerData.yearlyTokenLimit}) cannot exceed system settings limit (${systemYearlyTokenLimit})`
          );
        }
        yearlyTokenLimit = centerData.yearlyTokenLimit;
      }

      const timestamp = new Date().toISOString();
      const newCenter = {
        id: uuidv4(),
        name: centerData.name,
        description: centerData.description || '',
        blackoutDays: centerData.blackoutDays || [],
        yearlyTokenLimit: yearlyTokenLimit,
        createdAt: timestamp,
        updatedAt: timestamp,
      };

      const inserted = await supabaseService.insert(CENTERS_TABLE, newCenter);
      const createdCenter = Array.isArray(inserted) ? inserted[0] : inserted;

      logger.info('Center created successfully', {
        centerId: createdCenter?.id,
        yearlyTokenLimit,
        operation: 'createCenter',
      });

      return createdCenter || newCenter;
    } catch (error) {
      logger.error('Error creating center', error, { operation: 'createCenter' });
      throw error;
    }
  }

  async updateCenter(centerId, updates) {
    try {
      logger.info('Updating center', { centerId, updates, operation: 'updateCenter' });

      const current = await this.getCenterById(centerId);
      // Validate yearlyTokenLimit if being updated
      if (updates.yearlyTokenLimit !== undefined && updates.yearlyTokenLimit !== null) {
        const systemSettings = await settingsService.getSettings();
        const systemYearlyTokenLimit = systemSettings.yearlyTokenLimit || 12;

        if (updates.yearlyTokenLimit > systemYearlyTokenLimit) {
          throw new Error(
            `Center yearly token limit (${updates.yearlyTokenLimit}) cannot exceed system settings limit (${systemYearlyTokenLimit})`
          );
        }
      }

      if (!current) {
        throw new Error('Center not found');
      }

      if (updates.name && updates.name !== current.name) {
        const existing = await supabaseService.select(CENTERS_TABLE, {
          filters: [
            { column: 'name', operator: 'ilike', value: updates.name },
            { column: 'id', operator: 'neq', value: centerId },
          ],
        });

        if (Array.isArray(existing) && existing.length > 0) {
          throw new Error('Center with this name already exists');
        }
      }

      const payload = {
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      const updatedCenter = await supabaseService.update(CENTERS_TABLE, payload, {
        filters: [{ column: 'id', value: centerId }],
        single: true,
      });

      if (!updatedCenter) {
        throw new Error('Center not found');
      }

      logger.info('Center updated successfully', { centerId, operation: 'updateCenter' });
      return updatedCenter;
    } catch (error) {
      logger.error('Error updating center', error, { centerId, operation: 'updateCenter' });
      throw error;
    }
  }

  async deleteCenter(centerId) {
    try {
      logger.info('Deleting center', { centerId, operation: 'deleteCenter' });

      const deleted = await supabaseService.remove(CENTERS_TABLE, {
        filters: [{ column: 'id', value: centerId }],
        single: true,
      });

      if (!deleted) {
        throw new Error('Center not found');
      }

      logger.info('Center deleted successfully', { centerId, operation: 'deleteCenter' });
      return true;
    } catch (error) {
      logger.error('Error deleting center', error, { centerId, operation: 'deleteCenter' });
      throw error;
    }
  }

  async addBlackoutDay(centerId, date) {
    try {
      logger.info('Adding blackout day to center', { centerId, date, operation: 'addBlackoutDay' });

      const center = await this.getCenterById(centerId);
      if (!center) {
        throw new Error('Center not found');
      }

      const blackoutDays = Array.isArray(center.blackoutDays) ? [...center.blackoutDays] : [];
      if (!blackoutDays.includes(date)) {
        blackoutDays.push(date);
        blackoutDays.sort();
        await this.updateCenter(centerId, { blackoutDays });
      }

      return await this.getCenterById(centerId);
    } catch (error) {
      logger.error('Error adding blackout day', error, { centerId, date, operation: 'addBlackoutDay' });
      throw error;
    }
  }

  async removeBlackoutDay(centerId, date) {
    try {
      logger.info('Removing blackout day from center', { centerId, date, operation: 'removeBlackoutDay' });

      const center = await this.getCenterById(centerId);
      if (!center) {
        throw new Error('Center not found');
      }

      const blackoutDays = (center.blackoutDays || []).filter((d) => d !== date);
      await this.updateCenter(centerId, { blackoutDays });

      return await this.getCenterById(centerId);
    } catch (error) {
      logger.error('Error removing blackout day', error, { centerId, date, operation: 'removeBlackoutDay' });
      throw error;
    }
  }

  async getBlackoutDays(centerId) {
    try {
      logger.info('Fetching blackout days for center', { centerId, operation: 'getBlackoutDays' });

      const center = await this.getCenterById(centerId);
      if (!center) {
        return [];
      }

      return center.blackoutDays || [];
    } catch (error) {
      logger.error('Error fetching blackout days', error, { centerId, operation: 'getBlackoutDays' });
      return [];
    }
  }

  /**
   * Get the effective yearly token limit for a center
   * Returns center-specific limit if set, otherwise returns system default
   */
  async getEffectiveYearlyTokenLimit(centerId) {
    try {
      if (!centerId) {
        // No center specified, return system default
        const systemSettings = await settingsService.getSettings();
        return systemSettings.yearlyTokenLimit || 12;
      }

      const center = await this.getCenterById(centerId);
      if (!center) {
        logger.warning('Center not found, returning system default', { centerId });
        const systemSettings = await settingsService.getSettings();
        return systemSettings.yearlyTokenLimit || 12;
      }

      // If center has a yearlyTokenLimit set, use it; otherwise use system default
      if (center.yearlyTokenLimit !== null && center.yearlyTokenLimit !== undefined) {
        return center.yearlyTokenLimit;
      }

      const systemSettings = await settingsService.getSettings();
      return systemSettings.yearlyTokenLimit || 12;
    } catch (error) {
      logger.error('Error getting effective yearly token limit', error, { centerId });
      return 12; // Fallback to default
    }
  }
}

const centerService = new CenterService();
export default centerService;

